<script setup>
import { computed, defineProps, defineEmits } from "vue"
import { Dialog, TransitionRoot, TransitionChild } from "@headlessui/vue"
import { XMarkIcon, LinkIcon } from "@heroicons/vue/24/outline"
import { CheckBadgeIcon } from "@heroicons/vue/20/solid"

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  siret: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(["update:modelValue"])

const open = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    return emit("update:modelValue", value)
  },
})
</script>

<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-[99]" @close="open = false">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white p-7 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6"
            >
              <div class="flex justify-end">
                <button @click="open = false">
                  <XMarkIcon class="w-6 h-6 text-gray-600" />
                </button>
              </div>
              <div>
                <div class="flex flex-col items-center">
                  <div
                    class="flex justify-center items-center bg-orange-50 rounded h-14 w-14 p-3 mb-5"
                  >
                    <CheckBadgeIcon class="w-7 h-7 text-orange-500" />
                  </div>
                  <h4
                    class="text-xl font-medium leading-6 text-gray-900 text-center mb-2.5"
                  >
                    {{ $t("sign-up.existing-siret.title") }}
                  </h4>
                  <p class="text-sm text-gray-600 text-center">
                    {{ $t("sign-up.existing-siret.description", { siret }) }}
                  </p>
                </div>
                <div class="mt-5">
                  <router-link
                    :to="{ name: 'SignIn' }"
                    class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    <LinkIcon class="w-5 h-5 mr-2.5" />
                    {{ $t("sign-up.existing-siret.button") }}
                  </router-link>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped></style>
